import { useEffect } from "react";

import dayjs from "dayjs";
import PropTypes from "prop-types";
import { FormProvider, useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import { Stack, Switch, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";

import { DiscountTypeEnum } from "@/features/shared";
import { CenteredLoading } from "@/features/ui";
import { ExternalOtherSection } from "./external-other-section";
import { ExternalPriceSummarySection } from "./external-price-summary-section";
import { GeneralInformationSection } from "./general-information-section/general-information-section";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./inverted-lab-purchase-order-form-constants";
import { OtherSection } from "./other-section";
import { PriceSummarySection } from "./price-summary-section";
import { SalesOrderLensProductSection } from "./sales-order-lens-product-section";

export const InvertedLabPurchaseOrderForm = ({
  defaultValues,
  onSubmit,
  renderActionButton,
  isLoading = false,
}) => {
  const methods = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });
  const { reset, watch, setValue, getValues, handleSubmit, formState } =
    methods;

  useEffect(() => {
    if (!defaultValues) {
      return;
    }

    reset({ ...DEFAULT_VALUES, ...defaultValues });
  }, [defaultValues, reset]);

  const onFormSubmit = async (formData) => {
    const payload = {
      taxable: formData.taxable,
      totalIncludesTax: formData.totalIncludesTax,
      transactionDate: dayjs(formData.transactionDate).format("YYYY-MM-DD"),
      sellerableId: formData.seller.id,
      paymentTermId: formData.paymentTerm.id,
      note: formData.note || null,
      taxAmount: formData.taxAmount,
      taxPercentage: formData.taxPercentage,
      discountType: formData.discountType,
      discountAmount: formData.discountInput || 0,
      grandTotal: formData.grandTotal,
      salesOrderLensProductId: formData.salesOrderLensProductId,
      externalPurchaseOrder: formData.externalPurchaseOrder
        ? {
            taxable: formData.externalPurchaseOrder.taxable,
            totalIncludesTax: formData.externalPurchaseOrder.totalIncludesTax,
            sellerableId: formData.externalPurchaseOrder.supplier.id,
            paymentTermId: formData.externalPurchaseOrder.paymentTerm.id,
            note: formData.externalPurchaseOrder.note || null,
          }
        : null,
    };

    await onSubmit(payload);
  };

  const hasExternalPurchaseOrder = watch("hasExternalPurchaseOrder");

  const handleVariantEnabledChange = (e) => {
    const isChecked = e.target.checked;

    setValue("hasExternalPurchaseOrder", isChecked);

    if (isChecked) {
      setValue("externalPurchaseOrder", {
        supplier: null,
        paymentTerm: null,
        discount: 0,
        discountInput: 0,
        discountType: DiscountTypeEnum.FIXED_AMOUNT,
        taxable: false,
        totalIncludesTax: false,
        taxAmount: 0,
        note: getValues("note") || null,
      });
    } else {
      setValue("externalPurchaseOrder", null);
    }
  };

  return isLoading ? (
    <CenteredLoading sx={{ height: "100vh" }} />
  ) : (
    <FormProvider {...methods}>
      <Grid
        container
        component="form"
        noValidate
        onSubmit={handleSubmit(onFormSubmit)}
        spacing={2.5}
      >
        <Grid size={{ xs: 12 }}>
          <GeneralInformationSection />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <SalesOrderLensProductSection />
        </Grid>
        <Grid size={12}>
          <Typography variant="h6">Internal Purchase Order</Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 5, lg: 7 }}>
          <OtherSection />
        </Grid>
        <Grid size={{ xs: 12, md: 7, lg: 5 }}>
          <PriceSummarySection />
        </Grid>
        <Grid size={12}>
          <Stack direction="row" spacing={2} sx={{
            alignItems: "center"
          }}>
            <Typography variant="h6">External Purchase Order</Typography>
            <Switch
              checked={hasExternalPurchaseOrder}
              onChange={handleVariantEnabledChange}
            />
          </Stack>
        </Grid>
        {hasExternalPurchaseOrder && (
          <>
            <Grid size={{ xs: 12, md: 5, lg: 7 }}>
              <ExternalOtherSection />
            </Grid>
            <Grid size={{ xs: 12, md: 7, lg: 5 }}>
              <ExternalPriceSummarySection />
            </Grid>
          </>
        )}
        <Grid
          size={{ xs: 12 }}
          sx={{
            display: "flex",
            justifyContent: "flex-end"
          }}>
          {renderActionButton({ isSubmitting: formState.isSubmitting })}
        </Grid>
      </Grid>
    </FormProvider>
  );
};

InvertedLabPurchaseOrderForm.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func,
  renderActionButton: PropTypes.func,
  isLoading: PropTypes.bool,
};
